// SkillStones — the Skills-scene landmark. Five standing stones
// (cylinders 0.08r x 0.5h) arranged in a small arc, each painted a
// different palette color. They evoke a "stone circle" — calm, ancient,
// and vaguely ceremonial.

import { useMemo } from 'react';
import * as THREE from 'three';
import type { Group } from 'three';

// Palette anchors — mint, gold, warm, cool, muted. Re-uses the design
// system's accent + secondary + mint + gold + muted colors.
const STONE_COLORS = [
  '#7fb89f', // mint
  '#b8a572', // gold
  '#f5f0e8', // warm white
  '#5a5a5a', // muted
  '#ededed', // primary text
];

// Stone heights — slight variation so the arc looks natural.
const STONE_HEIGHTS = [0.5, 0.62, 0.7, 0.55, 0.48];

export interface SkillStonesProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  isActive?: boolean;
  isHovered?: boolean;
  groupRef?: React.MutableRefObject<Group | null>;
}

export function SkillStones({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  isActive = false,
  isHovered = false,
  groupRef,
}: SkillStonesProps) {
  // Stone offsets — arranged in a small arc of radius 0.45.
  const stonePositions = useMemo(() => {
    const out: [number, number, number][] = [];
    const ARC_RADIUS = 0.45;
    const ARC_HALF = Math.PI * 0.5; // 90° arc
    const N = 5;
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      // -ARC_HALF/2 .. +ARC_HALF/2
      const a = -ARC_HALF / 2 + ARC_HALF * t;
      const x = Math.sin(a) * ARC_RADIUS;
      const z = Math.cos(a) * ARC_RADIUS * 0.5; // squashed into ellipse
      out.push([x, 0, z]);
    }
    return out;
  }, []);

  const combinedScale = useMemo(() => {
    const base = scale;
    const hover = isHovered ? 1.08 : 1;
    const active = isActive ? 1.12 : 1;
    return base * hover * active;
  }, [scale, isHovered, isActive]);

  return (
    <group
      ref={groupRef as React.MutableRefObject<Group>}
      name="landmark-skill-stones"
      position={position}
      rotation={rotation}
      scale={combinedScale}
    >
      {stonePositions.map((pos, i) => {
        const h = STONE_HEIGHTS[i];
        const color = STONE_COLORS[i];
        const isMint = i === 0;
        return (
          <group key={i} position={pos}>
            {/* Stone body — slightly tapered cylinder for character. */}
            <mesh
              name={`stone-${i}`}
              position={[0, h / 2, 0]}
              userData={{ landmarkType: 'skill_stones', index: i }}
            >
              <cylinderGeometry args={[0.07, 0.085, h, 6]} />
              <meshStandardMaterial
                color={color}
                emissive={isMint ? '#7fb89f' : color}
                emissiveIntensity={isActive ? 0.4 : 0.1}
                roughness={0.9}
                flatShading
              />
            </mesh>

            {/* Cap — tiny cap on top to suggest a "headstone" shape. */}
            <mesh
              name={`stone-cap-${i}`}
              position={[0, h + 0.02, 0]}
              userData={{ landmarkType: 'skill_stones', index: i }}
            >
              <boxGeometry args={[0.14, 0.04, 0.14]} />
              <meshStandardMaterial color={color} roughness={0.85} flatShading />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

export default SkillStones;
